import { formatDayAndMonth, formatMonth } from "../i18n/format.js";
/**
 * The arithmetic behind the stacked-area chart, kept out of the component.
 *
 * Separated because these are the parts with rules that can be got subtly wrong, and a rule that
 * can be got wrong deserves a test that does not need a DOM, a chart library, or a rendered SVG to
 * express. The component's job is to draw what this returns.
 *
 * The behaviour is specified twice in the product documentation, identically:
 * `docs/project-overview/savings.md` §The Savings Chart and `dashboard.md` AC-D3/AC-D4. Both
 * consumers of this chart in v1 want exactly this shape, which is why there is one wrapper and not
 * a general charting abstraction.
 *
 * ============================================================================
 * CORRECTED BY FEATURE 006, ITS FIRST REAL CONSUMER, ON TWO COUNTS.
 * ============================================================================
 *
 * 004 shipped this against fixture data and recorded that the first real consumer would be the
 * test. It was, and two things failed.
 *
 *   1. LAYERS WERE KEYED BY DISPLAY NAME. `values` was built from `s.label`, so two savings
 *      accounts both called "Savings" — which 006 FR-002 explicitly permits, because two accounts
 *      at one bank with the same product name is ordinary — collapsed into one entry, last writer
 *      winning. A layer vanished and the top of the stack understated the household's money,
 *      silently. `ChartSeries` now carries an `id`.
 *
 *   2. THE WINDOW FLOORED AT THE EARLIEST DATA POINT. `savings.md` says the floor is HOUSEHOLD
 *      CREATION. For a household created ten months ago whose first snapshot is from last month,
 *      that drew one column where ten were specified — and it made the axis a function of the data,
 *      so adding a backdated snapshot silently changed the window. Both bounds now come from the
 *      caller.
 *
 * The `now` parameter is GONE with them. It was there so a caller could render a fixed period and a
 * test could assert one; the server deciding the window is strictly more of that, and it leaves this
 * module with no clock at all. That matters: the server resolves which positions exist, and a
 * component re-deriving them from its own clock can disagree across a boundary, ask for a position
 * that was never sent, and have carry-forward fill it with a value nobody computed.
 *
 * ============================================================================
 * AND ON A THIRD, ONCE REAL BALANCES WERE ON SCREEN.
 * ============================================================================
 *
 *   3. IT BUCKETED BY MONTH, WHICH HID DATA. A household recording three balances in July saw one
 *      point for July. Nine snapshots rendered as four points, on the chart built to show them.
 *      Points are now dated, so every entry is a vertex — and a range shorter than a year means
 *      something, where a one-month view of monthly buckets was a single dot.
 */

/**
 * A dated value. `at` is a calendar date, `YYYY-MM-DD`.
 *
 * It was `month` until feature 006 put real balances on screen. Bucketing by month meant a
 * household that recorded three balances in July saw ONE point for July — the last of the three —
 * so two thirds of what somebody had entered was invisible on the chart built to show it. Nine
 * snapshots rendered as four points.
 *
 * Dates also make a range shorter than a year mean something: a one-month view of monthly buckets
 * is a single dot.
 */
export interface SeriesPoint {
  at: string;
  value: number;
}

/** One layer of the stack. In 006 a savings account; in 013 a pillar. */
export interface ChartSeries {
  /**
   * Stable identity, distinct from the label.
   *
   * Two series may legitimately share a `label`. Keying anything by the label merges them; see the
   * header. Callers supply the identity they already have — an account id in 006 — rather than
   * inventing one, so the fix holds for 013 too.
   */
  id: string;
  label: string;
  points: SeriesPoint[];
}

/** One position on the x-axis, with every series' contribution resolved, keyed by series `id`. */
export interface ChartRow {
  at: string;
  values: Record<string, number>;
}

/**
 * The x positions to plot: every date any series has a point at, in order.
 *
 * Derived from the points rather than generated from bounds, and that is safe HERE in a way it was
 * not before. The 004 defect this replaces let a SPARSE series shrink the axis to its own data. The
 * server now sends DENSE series — every series carries a value at every x, and the window's own
 * endpoints are always among them — so the union is exactly the window the server decided, and no
 * caller can widen or narrow it by what it happens to hold.
 *
 * ISO dates sort lexicographically, so this needs no parsing and has no timezone in it.
 */
export function chartPositions(series: ChartSeries[]): string[] {
  const positions = new Set<string>();
  for (const s of series) for (const point of s.points) positions.add(point.at);
  return [...positions].sort();
}

/**
 * The value a series contributes at a given date: its most recent point at or before it.
 *
 * A gap does NOT interpolate, and a date before the series begins contributes 0 rather than being
 * absent. Both are specified — `savings.md` "Accounts with no snapshot at or before that point
 * contribute 0", `dashboard.md` AC-D4 — and the reason is financial rather than cosmetic: this
 * model is reconstructed from dated snapshots, so a gap means "nothing was recorded". Interpolating
 * would draw a balance nobody ever entered, on a chart people use to make decisions.
 *
 * Carrying the last known value FORWARD is the other half of the same rule: a balance recorded in
 * March is still the balance in April if nothing new was recorded. That is not invention — it is
 * what the most recent snapshot says.
 */
export function valueAt(series: ChartSeries, at: string): number {
  let latest: SeriesPoint | undefined;
  for (const point of series.points) {
    if (point.at <= at && (latest === undefined || point.at > latest.at)) {
      latest = point;
    }
  }
  return latest?.value ?? 0;
}

/** The chart's data, one row per x position with every series resolved and keyed by its `id`. */
export function toChartRows(series: ChartSeries[]): ChartRow[] {
  return chartPositions(series).map((at) => ({
    at,
    values: Object.fromEntries(series.map((s) => [s.id, valueAt(s, at)])),
  }));
}

/** True when there is nothing to draw — no series, or every series empty. */
export function isEmpty(series: ChartSeries[]): boolean {
  return series.every((s) => s.points.length === 0);
}

/**
 * `2026-08-09` → `9 Aug`, and `2026-08` → `Aug 2026`. What a reader wants on an axis, at whichever
 * resolution the series actually has.
 *
 * ============================================================================
 * WHY IT READS THE SHAPE INSTEAD OF TAKING A FLAG
 * ============================================================================
 *
 * A series is either daily or monthly, and the point strings already say which. Adding a `granularity`
 * prop would let a caller declare one and pass the other; a `YYYY-MM` point run through the daily
 * branch defaults the day to 1 and labels August "1 Aug" — a day that is not in the data, on a point
 * that is not about a day. Deriving it cannot disagree with itself.
 *
 * Built from the STRING and rendered in UTC. `new Date("2026-08-09")` parses as UTC midnight and
 * then formats in the viewer's timezone, so anybody west of Greenwich would see the 8th — for a
 * value that has no time in it and cannot be converted anywhere.
 */
export function axisLabel(at: string): string {
  const [year = "1970", m = "01", d] = at.split("-");
  // Feature 021: the reader's language decides how a date reads, resolved in `i18n/format.ts`.
  return d === undefined ? formatMonth(Number(year), Number(m), "short") : formatDayAndMonth(at);
}

/**
 * The y-axis for a comparison chart: fitted to the data, and labelled in round numbers.
 *
 * ==========================================================================================
 * TWO PROBLEMS, AND ONLY ONE OF THEM IS THE FRAME
 * ==========================================================================================
 *
 * A portfolio that moved between EUR 29,800 and EUR 30,200 on an axis from zero occupies the top one
 * per cent of the plot and reads as a flat line pinned to the ceiling. Everything that happened is
 * compressed out of existence, and the space below it says only that the household is not broke.
 *
 * Fitting the frame to the data fixes that and introduces a worse problem: an axis running from
 * 29,768 to 30,232 is divided into equal parts, and the labels come out as 29,861 and 29,954 —
 * numbers no one chose, that mean nothing, and that change every time the data does.
 *
 * So the frame is fitted AND the labels are rounded. The tick step is picked from the {1, 2, 2.5, 5}
 * x 10^n family — the steps people actually count in — and the bounds are moved outward to whole
 * multiples of it. This is what D3's `.nice()` does, and what a spreadsheet does, for the same
 * reason: an axis is read, not measured.
 *
 * ==========================================================================================
 * WHAT IS GIVEN UP, SAID PLAINLY
 * ==========================================================================================
 *
 * A fitted axis makes a one per cent wobble look like a mountain. The defence is that the labels now
 * say so — they read 29,800 to 30,200, in round numbers a reader can subtract — and that anybody
 * reading value-over-time is asking about shape. Every broker draws them this way.
 *
 * A STACK KEEPS ITS ZERO, which is why callers apply this to `lines` only. A stacked chart means the
 * layers add up to the total; lifting the floor cuts the bottom off the bottom layer and the
 * composition stops being true.
 */
export interface ChartAxis {
  /** Exact bounds, already rounded to whole steps. */
  domain: [number, number];
  /** Every label, computed here rather than left to the chart to interpolate. */
  ticks: number[];
}

/**
 * The nearest "nice" number at or above `value` — 1, 2, 2.5 or 5 times a power of ten.
 *
 * 2.5 is in the set because it is how money is counted: 250, 2,500, 25,000 all read as naturally as
 * 200 or 500, and without it a range that wants a 2,500 step is forced to 5,000 and halves the
 * number of labels.
 */
function niceStep(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalised = value / magnitude;
  const step =
    normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 2.5 ? 2.5 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

export function chartAxis(series: ChartSeries[], targetTicks = 5): ChartAxis | undefined {
  const values = series.flatMap((layer) => layer.points.map((point) => point.value));
  if (values.length === 0) return undefined;

  let min = Math.min(...values);
  let max = Math.max(...values);

  /*
   * A FLAT SERIES STILL NEEDS A RANGE. One price, or a year at exactly the same value, gives
   * min === max — a zero-height axis, on which the line is drawn along an edge or not at all. A
   * stock added today, with a single fetched price, is the common case.
   */
  if (min === max) {
    const spread = Math.abs(min) * 0.1 || 1;
    min -= spread;
    max += spread;
  }

  const step = niceStep((max - min) / Math.max(1, targetTicks - 1));

  /*
   * Rounded OUTWARD, then given a whole step of headroom if the data lands exactly on a bound.
   *
   * Without that, a maximum of exactly 30,000 with a step of 100 puts the line along the very top of
   * the plot — which is the complaint this function exists to answer, arrived at from the other
   * direction.
   */
  let floor = Math.floor(min / step) * step;
  let ceiling = Math.ceil(max / step) * step;
  if (ceiling === max) ceiling += step;
  if (floor === min) floor -= step;

  /*
   * A quantity that cannot be negative keeps its zero rather than showing an axis below it. A
   * portfolio growing from nothing is the case: floor lands at 0 naturally and stays there.
   */
  if (min >= 0 && floor < 0) floor = 0;

  const ticks: number[] = [];
  // Guarded on the count rather than on `value <= ceiling`: repeated addition of a fractional step
  // drifts, and a rounding error at the last tick would drop it or add a spurious one.
  const count = Math.round((ceiling - floor) / step);
  for (let index = 0; index <= count; index += 1) {
    ticks.push(Number((floor + index * step).toFixed(6)));
  }

  return { domain: [floor, ceiling], ticks };
}
